import React from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bot, Send, Sparkles, User } from "lucide-react";

export const QUICK_PROMPTS = [
  { label: "Practice Plan", prompt: "Draft tomorrow's practice card emphasizing inside run and our pursuit standards. Include timed periods and coach assignments." },
  { label: "Parent Email", prompt: "Write a weekly parent newsletter in our program's voice — warm but firm — recapping this week." },
  { label: "Pre-game Speech", prompt: "Write a 90-second pre-game speech that uses our core values by name." },
  { label: "Weekly Goals", prompt: "Set 5 measurable team goals for this coming week that reflect our program standards." },
  { label: "Meeting Agenda", prompt: "Build the full staff meeting agenda for Monday 7 AM." },
  { label: "Character Lesson", prompt: "Design a 10-minute character lesson for the team meeting this week." },
];

const slug = (label) => label.replace(/\s/g, "").toLowerCase();

export function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`msg-${role}`}>
      <div className={`w-8 h-8 shrink-0 flex items-center justify-center border ${isUser ? "border-slate-600 bg-surface" : "border-amber/40 bg-amber/10"}`}>
        {isUser ? <User className="w-4 h-4 text-slate-300" /> : <Bot className="w-4 h-4 text-amber" />}
      </div>
      <div className={`max-w-[80%] px-4 py-3 border ${isUser ? "border-surface-3 bg-surface" : "border-amber/30 bg-surface"}`}>
        {!isUser && <div className="text-[9px] font-condensed uppercase tracking-widest text-amber mb-1">Coach AI</div>}
        <div className="text-sm text-slate-100 whitespace-pre-wrap leading-relaxed">{content}</div>
      </div>
    </div>
  );
}

export function ChatEmptyState({ onPick }) {
  return (
    <div className="max-w-2xl mx-auto text-center py-16">
      <div className="inline-flex w-14 h-14 items-center justify-center border border-amber/50 bg-amber/10">
        <Bot className="w-7 h-7 text-amber" />
      </div>
      <div className="font-display text-4xl uppercase tracking-wide text-white mt-4">Ask Your Program</div>
      <div className="text-slate-400 text-sm mt-2 max-w-lg mx-auto">
        I know your mission, your standards, your playbook language. Ask me to draft a practice,
        write a parent email, or turn your core values into a speech.
      </div>
      <div className="hairline my-6 max-w-md mx-auto" />
      <div className="grid grid-cols-2 gap-2 max-w-lg mx-auto">
        {QUICK_PROMPTS.slice(0, 4).map(q => (
          <button
            key={q.label}
            onClick={() => onPick(q.prompt)}
            data-testid={`quick-prompt-${slug(q.label)}`}
            className="text-left px-4 py-3 border border-surface-3 hover:border-amber bg-surface hover:bg-surface-2 transition-colors"
          >
            <div className="text-[10px] font-condensed uppercase tracking-widest text-amber">{q.label}</div>
            <div className="text-xs text-slate-300 mt-1 line-clamp-2">{q.prompt}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ChatInput({ value, onChange, onSubmit, streaming }) {
  return (
    <form onSubmit={onSubmit} className="border-t border-surface-3 p-4 bg-surface" data-testid="ai-input-form">
      <div className="flex gap-3 items-end">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSubmit(e); } }}
          placeholder="Ask your program anything…"
          rows={2}
          data-testid="ai-input"
          className="flex-1 bg-background border-surface-3 focus-visible:ring-amber rounded-none resize-none text-sm"
        />
        <Button type="submit" disabled={streaming || !value.trim()} data-testid="ai-send"
          className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-[64px] px-6 font-condensed uppercase tracking-widest">
          <Send className="w-4 h-4 mr-1" /> {streaming ? "…" : "Send"}
        </Button>
      </div>
      <div className="text-[10px] text-slate-500 mt-2 font-condensed uppercase tracking-widest">
        Enter to send · Shift+Enter for new line
      </div>
    </form>
  );
}

export function QuickActionsSidebar({ onPick, disabled }) {
  return (
    <aside className="p-6 space-y-4 overflow-y-auto">
      <div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber" />
          <div className="font-condensed uppercase tracking-[0.25em] text-sm">Quick Actions</div>
        </div>
        <div className="hairline my-3" />
        <div className="space-y-2">
          {QUICK_PROMPTS.map(q => (
            <button
              key={q.label}
              onClick={() => onPick(q.prompt)}
              disabled={disabled}
              data-testid={`side-prompt-${slug(q.label)}`}
              className="w-full text-left px-3 py-2 border border-surface-3 hover:border-amber bg-surface hover:bg-surface-2 disabled:opacity-50 transition-colors"
            >
              <div className="text-[10px] font-condensed uppercase tracking-widest text-amber">{q.label}</div>
              <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{q.prompt}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <div className="font-condensed uppercase tracking-[0.25em] text-sm">Context Loaded</div>
        <div className="hairline my-3" />
        <div className="text-xs text-slate-400 space-y-1">
          <div>✓ Program mission & core values</div>
          <div>✓ Coaching philosophy</div>
          <div>✓ Staff manual policies</div>
          <div>✓ Player handbook standards</div>
        </div>
      </div>
    </aside>
  );
}
